import { motion, AnimatePresence } from 'framer-motion'
import { useState, useEffect } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import {
  FiShield,
  FiGrid,
  FiKey,
  FiInfo,
  FiDownload,
  FiCheckCircle,
  FiArrowRight
} from 'react-icons/fi'
import CreateAccountFlow from '../components/Auth/CreateAccountFlow'
import LoginWithSeed from '../components/Auth/LoginWithSeed'

export function LoginPage() {
  const { login, pubkey } = useAuth()
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [hasExtension, setHasExtension] = useState(true)
  const [showCreateFlow, setShowCreateFlow] = useState(false)
  const [showLoginFlow, setShowLoginFlow] = useState(false)
  const navigate = useNavigate()

  useEffect(() => {
    if (!window.nostr) {
      setHasExtension(false)
    }
  }, [])

  useEffect(() => {
    if (pubkey) {
      navigate('/')
    }
  }, [pubkey, navigate])

  const handleNostrLogin = async () => {
    setIsLoading(true);
    setError(null);
    try {
      await login();
    } catch (err: any) {
      setError(err.message || 'Giriş yapılamadı');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-surface-50 dark:bg-surface-950 px-4 py-12 selection:bg-mavi-500/30">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md"
      >
        {/* Logo Section */}
        <div className="text-center mb-10">
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            className="inline-block p-4 bg-mavi-500/10 dark:bg-mavi-500/20 rounded-3xl mb-6 backdrop-blur-xl border border-mavi-500/20 shadow-2xl shadow-mavi-500/10"
          >
            <img src="/img/icon.png" alt="Mavililer Logo" className="w-12 h-12" />
          </motion.div>
          <h1 className="text-3xl font-poppins font-bold text-gray-900 dark:text-white mb-2 tracking-tight">
            Mavililer Sözlük
          </h1>
          <p className="text-gray-500 dark:text-surface-400 font-merriweather italic text-sm">
            özgürlüğe ve merkeziyetsizliğe giriş yap.
          </p>
        </div>

        {/* Main Auth Card */}
        <div className="bg-white dark:bg-surface-900 rounded-[2.5rem] border border-surface-200 dark:border-surface-800 p-8 shadow-xl relative overflow-hidden group">
          {/* Error Display */}
          {error && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="mb-6 p-4 bg-kirmizi-50 dark:bg-kirmizi-900/10 border border-kirmizi-200 dark:border-kirmizi-800/20 rounded-2xl text-kirmizi-600 dark:text-kirmizi-400 text-xs text-center font-medium"
            >
              {error}
            </motion.div>
          )}

          <div className="space-y-4">
            {/* OPTION 1: SEED PHRASE (RECOMMENDED) */}
            <div className="relative">
              <div className="absolute -top-3 right-4 z-10">
                <span className="bg-mavi-500 text-[10px] font-poppins font-black uppercase tracking-tighter text-white px-2 py-0.5 rounded-full shadow-lg flex items-center gap-1">
                  <FiCheckCircle className="w-2.5 h-2.5" />
                  Önerilen
                </span>
              </div>
              <button
                onClick={() => setShowCreateFlow(true)}
                className="w-full group bg-mavi-500 hover:bg-mavi-600 text-white p-5 rounded-3xl transition-all duration-300 flex items-center justify-between shadow-xl shadow-mavi-500/20 font-poppins"
              >
                <div className="flex items-center gap-4 text-left">
                  <div className="bg-white/10 p-3 rounded-2xl group-hover:scale-110 transition-transform">
                    <FiGrid className="text-xl" />
                  </div>
                  <div>
                    <span className="block font-bold text-base leading-none mb-1">Hesap Oluştur</span>
                    <span className="text-[10px] text-mavi-100/70 block uppercase tracking-wide font-black">12 Kelime ile Başla</span>
                  </div>
                </div>
                <FiArrowRight className="text-xl text-mavi-200 group-hover:translate-x-1 transition-transform" />
              </button>
            </div>

            {/* OPTION 2: LOGIN WITH SEED */}
            <button
              onClick={() => setShowLoginFlow(true)}
              className="w-full group bg-surface-50 dark:bg-surface-800/50 hover:bg-white dark:hover:bg-surface-800 border border-surface-200 dark:border-surface-700 hover:border-mavi-500/50 text-gray-700 dark:text-white p-5 rounded-3xl transition-all duration-300 flex items-center justify-between font-poppins"
            >
              <div className="flex items-center gap-4 text-left">
                <div className="bg-white dark:bg-surface-700 p-3 rounded-2xl group-hover:bg-mavi-500/10 transition-colors shadow-sm">
                  <FiKey className="text-xl text-gray-400 group-hover:text-mavi-500 transition-colors" />
                </div>
                <div>
                  <span className="block font-bold text-base leading-none mb-1">Giriş Yap</span>
                  <span className="text-[10px] text-gray-500 dark:text-surface-400 block uppercase tracking-wide font-black">12 Kelime veya Anahtar</span>
                </div>
              </div>
              <FiArrowRight className="text-xl text-gray-300 dark:text-surface-600 group-hover:text-mavi-500 group-hover:translate-x-1 transition-all" />
            </button>

            <div className="relative py-2">
              <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-surface-100 dark:border-surface-800"></div></div>
              <div className="relative flex justify-center text-[10px] font-poppins font-black uppercase text-gray-400 dark:text-surface-600 tracking-[0.2em]"><span className="bg-white dark:bg-surface-900 px-4">VEYA</span></div>
            </div>

            {/* OPTION 3: NOSTR EXTENSION */}
            <button
              onClick={handleNostrLogin}
              disabled={isLoading}
              className="w-full group bg-surface-50 dark:bg-surface-800/50 hover:bg-white dark:hover:bg-surface-800 border border-surface-200 dark:border-surface-700 hover:border-mavi-500/30 text-gray-700 dark:text-white p-5 rounded-3xl transition-all flex items-center gap-4 font-poppins"
            >
              <div className="bg-white dark:bg-surface-700 p-3 rounded-2xl group-hover:scale-110 transition-transform shadow-sm">
                <FiShield className="text-xl text-mavi-500" />
              </div>
              <div className="text-left">
                <span className="block font-bold text-base leading-none mb-1">Uzantı ile Giriş</span>
                <span className="text-[10px] text-gray-500 dark:text-surface-400 block uppercase tracking-wide font-black">Alby, Nos2x vb.</span>
              </div>
            </button>

            {!hasExtension && (
              <div className="mt-4 p-4 bg-mavi-50 dark:bg-mavi-500/5 rounded-2xl border border-mavi-100 dark:border-mavi-500/10 flex items-start gap-4">
                <FiDownload className="text-mavi-500 shrink-0 mt-0.5 text-lg" />
                <div className="text-[11px] text-gray-600 dark:text-surface-400 leading-relaxed font-merriweather">
                  <span className="text-gray-900 dark:text-white font-poppins font-bold block mb-1 uppercase tracking-tighter">Uzantın Yok mu?</span>
                  <a href="https://getalby.com" target="_blank" rel="noreferrer" className="text-mavi-600 dark:text-mavi-400 hover:underline font-bold">Alby</a> veya <a href="https://nos2x.com" target="_blank" rel="noreferrer" className="text-mavi-600 dark:text-mavi-400 hover:underline font-bold">nos2x</a> kurarak şifresiz özgürlüğe adım atabilirsin.
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Footer Links */}
        <div className="mt-8 flex flex-col items-center gap-4 font-poppins">
          <Link
            to="/about"
            className="flex items-center gap-2 text-sm text-gray-500 dark:text-surface-500 hover:text-mavi-500 dark:hover:text-mavi-400 transition-all font-bold group"
          >
            <FiInfo className="text-lg group-hover:rotate-12 transition-transform" />
            <span>Mavililer Nedir? Nasıl Çalışır?</span>
          </Link>

          <Link
            to="/"
            className="text-xs text-gray-400 dark:text-surface-600 hover:text-gray-600 dark:hover:text-surface-400 transition-colors"
          >
            ← Göz atmaya devam et
          </Link>
        </div>
      </motion.div>

      {/* Modal Components */}
      <AnimatePresence>
        {showCreateFlow && (
          <CreateAccountFlow onClose={() => setShowCreateFlow(false)} />
        )}
        {showLoginFlow && (
          <LoginWithSeed onClose={() => setShowLoginFlow(false)} />
        )}
      </AnimatePresence>
    </div>
  )
}
